import React, { useEffect, useState } from 'react'
import { Controller, FormContext, useForm } from 'react-hook-form'
import PropTypes from 'prop-types'
import { Alert, AlertTitle } from '@material-ui/lab'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  TextField,
  Typography,
} from '@material-ui/core'

import { BaseDialog, FileUploadInput } from 'components'

import useSnackbar from 'hooks/useSnackbar'

import schema from './schema'
import { routes } from 'Routes'
import useStyles from './styles'
import * as service from 'service'
import helpers from 'helpers'

const InformDataModal = ({ ticketId, setLoading, refresh }) => {
  const defaultValues = {
    content: '',
    attachments: null,
    hasSend: false,
  }

  const [openModal, setOpenModal] = useState(false)
  const [savedValues, setSavedValues] = useState(null)

  const classes = useStyles()
  const snackbar = useSnackbar()
  const formMethods = useForm({
    validationSchema: schema,
    defaultValues,
  })

  const { control, handleSubmit, errors, watch, reset, getValues } = formMethods

  const hasSend = watch('hasSend')

  const handleOpenModal = () => setOpenModal((prevState) => !prevState)

  const handleRedirect = () => {
    window.open(
      routes.faqsDocumentations.show.replace(':faqsDocumentationId', '101'),
    )
  }

  const onSubmit = async (data) => {
    try {
      setLoading(true)

      await service.dponet.titularTickets.submitData({
        ticketId,
        ...data,
      })

      snackbar.open({
        message: 'Informações enviadas com sucesso!',
        variant: 'success',
      })

      reset()
      refresh()
      setOpenModal(false)
    } catch (error) {
      console.error(error)
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (hasSend) {
      setSavedValues(getValues())
      reset({ ...defaultValues, hasSend: true })
    } else if (savedValues) {
      reset({ ...savedValues, hasSend: false })
      setSavedValues(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSend])

  return (
    <Box className={classes.container}>
      <Typography variant="h6">
        Após confirmar que sua empresa possui dados relacionados ao titular,
        informe quais dados estão disponíveis em sua empresa.
      </Typography>
      <Button variant="outlined" color="inherit" onClick={handleOpenModal}>
        Informar
      </Button>
      {openModal && (
        <BaseDialog
          open={openModal}
          setOpen={setOpenModal}
          preventCloseOnBackdropClick
          title="Informar dados do titular"
          formRef="inform-data-subject-form"
          closeButtonText="Cancelar"
          actionButtonText="Enviar"
          actionButton={handleSubmit(onSubmit)}
        >
          <Box className={classes.content} py={2}>
            <Alert severity="info">
              <AlertTitle>Analise o tipo de pedido</AlertTitle>
              <Typography variant="body2" gutterBottom>
                Forneça o máximo de informações sobre o titular, para que o
                Encarregado de Proteção de Dados(DPO) possa responder à
                solicitação. Confira o tipo de pedido para fornecer informações
                compatíveis com os dados que existem na sua organização.
              </Typography>
              Para mais informações, consulte a nossa&nbsp;
              <strong onClick={handleRedirect} className={classes.link}>
                Central de Ajuda
              </strong>
              .
            </Alert>
            <FormContext {...formMethods}>
              <form
                onSubmit={handleSubmit(onSubmit)}
                id="inform-data-subject-form"
              >
                <Box className={classes.content}>
                  <Controller
                    control={control}
                    name="content"
                    mode="onChange"
                    as={
                      <TextField
                        type="text"
                        color="primary"
                        variant="outlined"
                        error={!!errors.content}
                        helperText={errors.content?.message}
                        multiline
                        minRows={10}
                        maxRows={18}
                        fullWidth
                        placeholder="Descreva os dados disponíveis..."
                        disabled={hasSend}
                      />
                    }
                  />
                  <FileUploadInput
                    controlName="attachments"
                    hasDownloadFile
                    controllerBar
                    multiple
                    disabled={hasSend}
                  />
                  <Box>
                    <Controller
                      control={control}
                      name="hasSend"
                      mode="onChange"
                      as={
                        <FormControl>
                          <FormControlLabel
                            control={<Checkbox />}
                            label="Já enviei essas informações à organização."
                          />
                          {!!errors?.hasSend && (
                            <FormHelperText error={!!errors?.hasSend}>
                              {errors?.hasSend?.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      }
                    />
                    {hasSend && (
                      <Alert severity="warning">
                        Você está informando que já enviou essas informações
                        para a organização. Certifique-se de que o envio foi
                        realizado corretamente, seja por este formulário ou
                        pelas mensagens da solicitação.
                      </Alert>
                    )}
                  </Box>
                </Box>
              </form>
            </FormContext>
          </Box>
        </BaseDialog>
      )}
    </Box>
  )
}

InformDataModal.propTypes = {
  ticketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  setLoading: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,
}

export default InformDataModal
